/**
 * Prompt groups — a real group with a random rule, or one pinned rule,
 * plus how several groups are handed out across drops.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { HintDot, SegmentedPills } from '@/components/ui';
import { useTextPromptGroups } from '@/features/campaign-texts/hooks/useTextPromptGroups';

import type { ContainerEditor } from '../state/useContainerEditor';
import { PromptGroupPicks } from './picks';

export const CG_COLORS = ['#f59e0b', '#0ea5e9', '#8b5cf6', '#ec4899', '#14b8a6', '#22c55e'];
const RANDOM_PATTERN = [1, 0, 0, 2, 0, 1];
const MICRO = 'flex w-28 shrink-0 items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground';

export const PromptGroupsStep: FC<{ editor: ContainerEditor }> = ({ editor }) => {
  const groups = useTextPromptGroups();
  const { creative } = editor.draft;
  const all = groups.data ?? [];
  const count = creative.textGroupIds.length;
  const dotIndex = (i: number) =>
    creative.assignment === 'ROTATE' ? i % count : RANDOM_PATTERN[i] % count;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <span className={`${MICRO} mt-1.5`}>Groups</span>
        <PromptGroupPicks groups={all} ids={creative.textGroupIds} onChange={(textGroupIds) => editor.creativePatch({ textGroupIds })} />
      </div>
      {count === 0 ? (
        <p className="text-xs text-muted-foreground">
          No group yet. Add one, then keep a random rule inside it or pin one rule.
        </p>
      ) : count > 1 ? (
        <>
          <div className="flex items-center gap-3">
            <span className={MICRO}>
              Group per drop
              <HintDot label="With several groups, each drop takes the next group, or a random group. A group set to Random rule then picks one of its rules." />
            </span>
            <SegmentedPills
              options={[
                { value: 'ROTATE', label: 'Rotate' },
                { value: 'RANDOM', label: 'Random' },
              ]}
              value={creative.assignment}
              onChange={(assignment) => editor.creativePatch({ assignment })}
            />
          </div>
          <div className="flex items-center gap-3">
            <span className={MICRO}>Next drops</span>
            <span className="flex items-center gap-1">
              {RANDOM_PATTERN.map((_, i) => (
                <span
                  key={i}
                  className="h-3 w-3 rounded-sm transition-colors duration-300"
                  style={{ backgroundColor: CG_COLORS[dotIndex(i) % CG_COLORS.length] }}
                />
              ))}
              <span className="ml-1.5 text-[11px] text-muted-foreground">
                {creative.assignment === 'ROTATE' ? 'strict rotation' : 'random draw per drop'}
              </span>
            </span>
          </div>
        </>
      ) : null}
    </div>
  );
};
